/**
 * Lightweight Remotion Player — no controls, fills its container 100%.
 * Used as the edit-mode background inside EditorCanvas so the actual
 * composition is visible behind the interactive layer overlays.
 */
import React, { useEffect, useRef } from 'react';
import { Player } from '@remotion/player';
import type { PlayerRef } from '@remotion/player';
import { NewsComposition } from '../../renderer/compositions/NewsComposition';
import type { CompositionProps } from '../../types';

interface PlayerBackgroundProps {
  compositionProps: CompositionProps;
  width: number;
  height: number;
  durationInFrames: number;
  fps: number;
  /** If true the player loops and plays automatically (edit-mode live preview). */
  livePreview?: boolean;
}

export function PlayerBackground({
  compositionProps,
  width,
  height,
  durationInFrames,
  fps,
  livePreview = false,
}: PlayerBackgroundProps) {
  const playerRef = useRef<PlayerRef>(null);

  // In livePreview (edit) mode: auto-play and loop so all layers are visible.
  useEffect(() => {
    if (!livePreview) return;
    const p = playerRef.current;
    if (!p) return;
    // Small delay so the player has mounted and decoded the first frame
    const id = setTimeout(() => { try { p.play(); } catch { /* ignore */ } }, 100);
    return () => clearTimeout(id);
  }, [livePreview]);

  // Start paused at 2 s in so all enter-animations have completed.
  // In live-preview mode, the play() call above will begin playing from there.
  const initialFrame = Math.min(fps * 2, Math.floor(durationInFrames * 0.1));

  return (
    <Player
      ref={playerRef}
      component={NewsComposition as unknown as React.ComponentType<Record<string, unknown>>}
      compositionWidth={width}
      compositionHeight={height}
      durationInFrames={durationInFrames}
      fps={fps}
      inputProps={compositionProps as unknown as Record<string, unknown>}
      style={{ width: '100%', height: '100%', display: 'block' }}
      clickToPlay={false}
      controls={false}
      showVolumeControls={false}
      doubleClickToFullscreen={false}
      initiallyMuted
      initialFrame={initialFrame}
      loop={livePreview}
    />
  );
}
